// A non-literal `string` template carries no placeholder info (e.g. a
// template resolved from a union of catalog keys), so fall back to a loose
// args record instead of the no-placeholder `Record<string, never>`.
export type TextArguments<T> = string extends T
  ? Record<string, string | number>
  : // eslint-disable-next-line @typescript-eslint/ban-types
    TextArgumentsRecursive<T, {}>

export type TextInterpolationPart = {
  text: string
  key?: string
  isPlaceholder: boolean
  isMarked: boolean
}

export type TextInterpolationPartsOptions = {
  markedKeys?: readonly string[]
}

export type TextInterpolationResult = {
  text: string
  parts: TextInterpolationPart[]
}

/**
 * Replaces placeholders in the text with the provided arguments
 *
 * You can use the following syntax for placeholders:
 *   Single curly braces for simple replacements:
 *     {key:number} - for numbers
 *     {name:string} - for strings
 *
 *   Double curly braces for plural replacements:
 *     {{count:buildings|buildings}} or short syntax building{{count:s}}
 *
 *   @see libs/shared/v2-common/src/__tests__/textInterpolation.spec.ts for examples
 */
export function interpolate<T extends string>(text: T, data: TextArguments<T>) {
  return parseTextParts(text)
    .map((part) => {
      if (typeof part === "string") {
        return part
      }

      return interpolatePart(part, data)
    })
    .join("")
}

export function interpolateParts<T extends string>(
  text: T,
  data: TextArguments<T>,
  options: TextInterpolationPartsOptions = {},
): TextInterpolationPart[] {
  return interpolateParsedParts(parseTextParts(text), data, options)
}

export function interpolateBoth<T extends string>(
  text: T,
  data: TextArguments<T>,
  options: TextInterpolationPartsOptions = {},
): TextInterpolationResult {
  const parts = interpolateParsedParts(parseTextParts(text), data, options)
  return {
    text: parts.map((part) => part.text).join(""),
    parts,
  }
}

export function plainTextInterpolationPart(
  text: string,
): TextInterpolationPart {
  return {
    text,
    isPlaceholder: false,
    isMarked: false,
  }
}

function interpolateParsedParts<T extends string>(
  parts: ParsedTextPart[],
  data: TextArguments<T>,
  options: TextInterpolationPartsOptions,
): TextInterpolationPart[] {
  const markedKeys = options.markedKeys ?? []
  return parts.map((part): TextInterpolationPart => {
    if (typeof part === "string") {
      return plainTextInterpolationPart(part)
    }

    const value = interpolatePart(part, data)
    if ("type" in part) {
      return {
        text: value,
        key: part.key,
        isPlaceholder: true,
        isMarked: markedKeys.includes(part.key),
      }
    }

    return {
      text: value,
      key: part.key,
      isPlaceholder: true,
      isMarked: false,
    }
  })
}

type TextArgumentsRecursive<T, Args> =
  T extends `${string}{${infer InterpolationStart}`
    ? InterpolationStart extends `{${infer InterpolationName}:${string}}}${infer Rest}`
      ? TextArgumentsRecursive<
          Rest,
          Args & {
            [key in Trim<InterpolationName>]: number
          }
        >
      : InterpolationStart extends `${infer InterpolationName}:${infer InterpolationValue}}${infer Rest}`
        ? TextArgumentsRecursive<
            Rest,
            Args & {
              [key in Trim<InterpolationName>]: TypeNameToType<
                Trim<InterpolationValue>
              >
            }
          >
        : // Untyped placeholder `{name}` — supported by the runtime parser
          // (BasicArgumentPart.type is optional), so accept both value types.
          InterpolationStart extends `${infer InterpolationName}}${infer Rest}`
          ? TextArgumentsRecursive<
              Rest,
              Args & {
                [key in Trim<InterpolationName>]: string | number
              }
            >
          : TextArgumentsRecursive<InterpolationStart, Args>
    : Flatten<Args>

type TypeNameToType<T> = T extends "string"
  ? string
  : T extends "number"
    ? number
    : { error: "not a number or string" }

type Trim<T extends string> = T extends ` ${infer U}`
  ? Trim<U>
  : T extends `${infer U} `
    ? Trim<U>
    : T

// eslint-disable-next-line @typescript-eslint/ban-types
type Flatten<T> = {} extends T
  ? Record<string, never>
  : { [K in keyof T]: T[K] }

const INTERPOLATION_REGEX = /({(?:[^{}]+|{(?:[^{}]+)*})*})/g

type BasicArgumentPart = {
  key: string
  type: string | undefined
}

type BasicPluralPart = {
  key: string
  zero?: string
  one: string
  two?: string
  few?: string
  many?: string
  rest: string
}

type ParsedTextPart = string | BasicArgumentPart | BasicPluralPart

function parseTextParts<T extends string>(text: T): ParsedTextPart[] {
  return text.split(INTERPOLATION_REGEX).map((part) => {
    // .match() instead of .test() to avoid problems with regex state
    // eslint-disable-next-line unicorn/prefer-regexp-test
    if (!part.match(INTERPOLATION_REGEX)) {
      return part
    }

    const content = removeOuterBrackets(part)

    if (content.startsWith("{")) {
      return parsePluralPart(removeOuterBrackets(content))
    }

    return parseArgumentPart(content)
  })
}

function interpolatePart<T extends string>(
  part: BasicArgumentPart | BasicPluralPart,
  data: TextArguments<T>,
): string {
  if ("type" in part) {
    const value = data[part.key as keyof typeof data]
    return String(value ?? "")
  }

  const count = Number(data[part.key as keyof typeof data])
  if (Number.isNaN(count)) {
    throw new TypeError(`Expected number for plural key: ${part.key}`)
  }
  return getPluralValue(part, count).replace("##", String(count))
}

function removeOuterBrackets(text: string) {
  return text.slice(1, -1)
}

function parseArgumentPart(text: string): BasicArgumentPart {
  const [key, type] = text.split(":").map((t) => t.trim())
  return { key, type }
}

function parsePluralPart(content: string): BasicPluralPart {
  const [keyOriginal, values = ""] = content.split(":")
  const key = keyOriginal.trim()

  const entries = values.split("|")

  const nrOfEntries = entries.filter((entry) => entry !== undefined).length

  if (nrOfEntries === 1) {
    return { key, rest: entries[0], one: "" }
  }
  if (nrOfEntries === 2) {
    return { key, one: entries[0], rest: entries[1] }
  }
  if (nrOfEntries === 3) {
    return { key, zero: entries[0], one: entries[1], rest: entries[2] }
  }

  return {
    key,
    zero: entries[0],
    one: entries[1],
    two: entries[2],
    few: entries[3],
    many: entries[4],
    rest: entries[5],
  }
}

function getPluralValue(part: BasicPluralPart, count: number): string {
  if (count === 0 && part.zero !== undefined) {
    return part.zero
  }
  if (count === 1 && part.one !== undefined) {
    return part.one
  }
  if (count === 2 && part.two !== undefined) {
    return part.two
  }
  if (count >= 3 && count <= 17 && part.few !== undefined) {
    return part.few
  }
  if (count >= 18 && part.many !== undefined) {
    return part.many
  }
  return part.rest
}
